import { useContext, useState } from "react";

import { Alert, Span } from "@icg360/design-system";

import { SpinnerLoader } from "components/loader/loader";
import { MultiStepFormLayout } from "components/multi-step-form-layout";
import { AuthAppContext } from "components/root/auth-app-provider";
import { CONFIG } from "config";
import {
  ABOVE_GROUND_POOL_STEP_COUNT,
  IN_GROUND_POOL_STEP_COUNT,
  NO_POOL_STEP_COUNT,
  POOL_DISPLAY_ENUM_MAP,
} from "consts";
import { useUserPropertyProfileDataQuery } from "gql/__generated__/hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { logError, trackEvent, useFlags } from "utils";

import {
  PropertyUpdatePoolContext,
  type PropertyUpdatePoolContextState,
} from ".";
import { FileThumbnails } from "../file-thumbnails";
import { usePropertyUpdatePool } from "./hooks";
import styles from "./property-update-pool.module.scss";

const POLLING_TIMEOUT = 30000; // Thirty seconds
const POLLING_INTERVAL = 1000; // One second

export const PoolReview = () => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>();
  const [_pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const { supportPhoneNumber } = useFlags();
  const { selectedPolicyId, userDetails, userInfo } =
    useContext(AuthAppContext);
  const { email: userEmail } = userInfo ?? {};
  const { keystonePolicyId: insightPolicyId } = userDetails ?? {};
  const { prevPoolType } = usePropertyUpdatePool();
  const { formNavigate, state } =
    useMultiStepFormState<PropertyUpdatePoolContextState>(
      PropertyUpdatePoolContext
    );
  const { startPolling, stopPolling } = useUserPropertyProfileDataQuery({
    variables: {
      policyID: selectedPolicyId,
    },
  });
  const { files, poolType } = state ?? {};

  const handleSubmit = async () => {
    setSubmitting(true);
    const formData = new FormData();
    const request = {
      insightPolicyId,
      userEmail,
      propertySection: "POOL",
      propertyUpdateItems: [
        {
          termName: "Pool Type",
          enumerationValue: POOL_DISPLAY_ENUM_MAP[poolType],
          currentDisplayValue: prevPoolType ?? "N/A",
          updatedDisplayValue: poolType,
        },
        // TODO: Rest of values...
      ],
    };
    formData.append("request", JSON.stringify(request));

    for (const file of files) {
      formData.append("file", file);
    }

    try {
      const response = await fetch(
        `${CONFIG.KEYSTONE_PROXY_HREF}/api/property/update`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (response.ok) {
        startPolling(POLLING_INTERVAL);
        setPollingTimeout(
          setTimeout(() => {
            stopPolling();
            formNavigate("../success");
          }, POLLING_TIMEOUT)
        );
      } else {
        setSubmitting(false);
        setError(response?.statusText);
        logError(`Pool update submission error: ${response?.statusText}`);
        trackError({
          errorMessage: response?.statusText,
          errorType: "server_error",
        });
      }
    } catch (err) {
      setSubmitting(false);
      setError(err);
      logError(`Pool update submission error: (${err?.name}: ${err?.message})`);
      trackError({
        errorCode: err?.code,
        errorMessage: err?.message,
        errorType: "system_error",
      });
    }
  };

  // TODO: Poll until data is updated in useEffect.

  type PropertyErrorProps = {
    errorCode?: string | number;
    errorMessage?: string;
    errorType?: string;
  };

  const trackError = ({
    errorCode,
    errorMessage,
    errorType,
  }: PropertyErrorProps) => {
    const fileNames: string[] = [];
    const fileTypes: string[] = [];
    const fileSizes: number[] = [];
    state?.files.forEach((file: File) => {
      fileNames.push(file.name);
      fileTypes.push(file.type);
      fileSizes.push(file.size);
    });
    trackEvent("PHE_Error", {
      id: "pool-update-submit-failed",
      numberOfFiles: state?.files?.length,
      errorCode,
      errorMessage,
      errorType,
      fileNames,
      fileTypes,
      fileSizes,
    });
  };

  if (submitting) {
    return (
      <div className={styles.loadingSpinner}>
        <SpinnerLoader firstMessage="Hang tight, we're sending your updates..." />
      </div>
    );
  }

  let totalSteps = NO_POOL_STEP_COUNT;
  if (state?.poolType === "In-ground") {
    totalSteps = IN_GROUND_POOL_STEP_COUNT;
  } else if (state?.poolType === "Above-ground") {
    totalSteps = ABOVE_GROUND_POOL_STEP_COUNT;
  }

  return (
    <MultiStepFormLayout
      step={totalSteps}
      totalSteps={totalSteps}
      heading="Does this look right?"
      buttonProps={{
        primary: {
          onPress: handleSubmit,
          text: "Yes, submit",
        },
        cancel: { path: "/my/property" },
        formNavigate,
      }}
    >
      <div className={styles.poolReview}>
        {error ? (
          <Alert
            appearance="danger"
            title="There was an error submitting your update."
            description={`Please try again. If the problem persists, contact support at ${supportPhoneNumber}.`}
          />
        ) : null}
        <Span>
          Swimming pool type: <Span bold>{state?.poolType}</Span>
        </Span>

        {state?.poolType !== "None" ? (
          <>
            {state?.poolType === "In-ground" ? (
              <>
                <Span>
                  Fenced: <Span bold>{state?.poolFence}</Span>
                </Span>
                {state?.poolFence === "Yes" ? (
                  <>
                    <Span>
                      Fence at least 4ft tall:{" "}
                      <Span bold>{state?.fenceFourFeet}</Span>
                    </Span>
                    <Span>
                      Fence with locking gate:{" "}
                      <Span bold>{state?.fenceLockingGate}</Span>
                    </Span>
                  </>
                ) : null}
              </>
            ) : null}
            <Span>
              Diving board: <Span bold>{state?.divingBoard}</Span>
            </Span>
            <Span>
              Slide: <Span bold>{state?.slide}</Span>
            </Span>
            <Span>
              Filled: <Span bold>{state?.filled}</Span>
            </Span>
            <Span>
              Attached to decking: <Span bold>{state?.poolDecking}</Span>
            </Span>
            {state?.poolDecking === "Yes" ? (
              <Span>
                Decking has locking gate:{" "}
                <Span bold>{state?.poolDeckGateLock}</Span>
              </Span>
            ) : null}
          </>
        ) : null}
        <div>
          <Span>Proof of update:</Span>
          <FileThumbnails files={state?.files} />
        </div>
      </div>
    </MultiStepFormLayout>
  );
};
